(function () {
  'use strict';

  const VIEWS = Object.freeze(['orders', 'order-detail', 'credits', 'coupons', 'profile', 'support']);
  const FILTERS = Object.freeze({
    orders: ['all', 'in_transit', 'delivered', 'cancelled'],
    coupons: ['available', 'used', 'expired']
  });
  const STATES = Object.freeze(['loading', 'ready', 'empty', 'partial', 'error']);
  const ABORTED = 'AbortError';
  const SESSION_REVALIDATION_INTERVAL_MS = 30000;
  const text = (value) => value == null ? '' : String(value);
  window.PersonalCenterViewModules = window.PersonalCenterViewModules || {};

  function formatMoney(value) {
    if (!value || typeof value !== 'object' || value.amount === null || value.amount === undefined || !text(value.currencyCode).trim()) return '';
    const amount = Number(value.amount);
    if (!Number.isFinite(amount)) return '';
    try {
      return new Intl.NumberFormat('es-MX', { style: 'currency', currency: text(value.currencyCode) }).format(amount);
    } catch (_) {
      return `${text(value.amount)} ${text(value.currencyCode)}`.trim();
    }
  }

  function adapterFailure(code, message) {
    return Object.assign(new Error(message), { code });
  }

  function isAbort(error) { return error?.name === ABORTED; }

  function isAuthenticationFailure(error) {
    const code = text(error?.code).trim();
    return error?.status === 401
      && error?.envelope?.success === false
      && error?.envelope?.error?.code === code
      && (code === 'UNAUTHORIZED' || code === 'AUTH_REQUIRED');
  }

  function customerIdentity(data) {
    return text(data?.profile?.id).trim();
  }

  function currentModules() {
    return window.PersonalCenterViewModules;
  }

  function orderDetailEnabled(root) {
    return root?.dataset?.pcOrderDetailEnabled === 'true';
  }

  function responseIsPartial(response) {
    if (!response || typeof response !== 'object') return false;
    if (response.meta?.partial === true) return true;
    return Object.values(response).some((value) => value && typeof value === 'object' && value.success === true && value.meta?.partial === true);
  }

  function responseIsEmpty(response) {
    return response?.success === true && Array.isArray(response.data) && response.data.length === 0;
  }

  function requestedState(rendered, response) {
    const state = typeof rendered === 'string' ? rendered : rendered?.state;
    if (STATES.includes(state) && state !== 'loading') return state;
    if (responseIsPartial(response)) return 'partial';
    return responseIsEmpty(response) ? 'empty' : 'ready';
  }

  class UnavailableAdapter {
    request() { return Promise.reject(adapterFailure('APP_PROXY_UNAVAILABLE', 'El adaptador de cuenta no está disponible.')); }
  }

  class PersonalCenterShell extends HTMLElement {
    constructor() {
      super();
      this.state = {
        adapter: null,
        adapterMode: '',
        activeView: 'orders',
        activeFilter: '',
        connected: false,
        controllers: new Map(),
        intent: 0,
        listeners: [],
        params: {},
        overview: null,
        loginBoundary: false,
        logoutTrigger: null,
        logoutUrl: '',
        pendingModules: new Set(),
        moduleTimers: new Map(),
        mobileNavMedia: null,
        mobileNavSync: null,
        mobileNavTarget: null,
        headerOffsetFrame: 0,
        lastSessionValidationAt: 0
      };
      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onLogoutDialogClose = this.onLogoutDialogClose.bind(this);
      this.onModuleReady = this.onModuleReady.bind(this);
      this.onPopstate = this.onPopstate.bind(this);
      this.onPageShow = this.onPageShow.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onHeaderOffsetChange = this.onHeaderOffsetChange.bind(this);
    }

    connectedCallback() {
      if (this.state.connected) {
        this.reinitialize();
        return;
      }
      this.state.connected = true;
      this.moveDialogsToPortal();
      this.refreshRefs();
      this.ensureAdapter();
      this.setupMobileNav();
      this.addListener(this, 'click', this.onClick, { capture: true });
      this.addListener(this, 'change', this.onChange);
      this.addListener(this, 'submit', this.onSubmit);
      this.addListener(this, 'keydown', this.onKeydown);
      if (this.refs.logoutDialog) this.addListener(this.refs.logoutDialog, 'close', this.onLogoutDialogClose);
      this.addListener(window, 'personal-center:module-ready', this.onModuleReady);
      this.addListener(window, 'popstate', this.onPopstate);
      this.addListener(window, 'pageshow', this.onPageShow);
      this.addListener(window, 'scroll', this.onHeaderOffsetChange, { passive: true });
      this.addListener(window, 'resize', this.onHeaderOffsetChange, { passive: true });
      this.addListener(document, 'visibilitychange', this.onVisibilityChange);
      this.addEditorListeners();
      this.syncStickyHeaderOffset();
      this.dataset.pcEnhanced = 'true';
      if (document.readyState === 'loading') {
        this.addListener(document, 'DOMContentLoaded', () => {
          if (this.state.connected && !this.state.loginBoundary) this.initializeRoute();
        }, { once: true });
      } else {
        this.initializeRoute();
      }
    }

    disconnectedCallback() {
      this.teardown();
    }

    refreshRefs() {
      this.refs = {
        status: this.querySelector('[data-pc-status]'),
        views: Array.from(this.querySelectorAll('[data-pc-view]')),
        links: Array.from(this.querySelectorAll('[data-pc-view-link]')),
        logoutDialog: this.querySelector('[data-pc-logout-dialog]'),
        mobileNav: this.querySelector('[data-pc-mobile-nav]'),
        mobileNavSummary: this.querySelector('[data-pc-mobile-nav] > summary')
      };
    }

    moveDialogsToPortal() {
      const portal = this.querySelector('[data-pc-dialog-portal]');
      if (!portal) return;
      const dialogIds = new Set();
      this.querySelectorAll('[data-pc-dialog]').forEach((dialog) => {
        const owner = dialog.dataset.pcDialogOwner || dialog.closest('[data-pc-view]')?.dataset.pcView || '';
        if (owner) dialog.dataset.pcDialogOwner = owner;
        const id = `${owner}:${dialog.dataset.pcDialog || ''}`;
        if (dialogIds.has(id)) {
          dialog.remove();
          return;
        }
        dialogIds.add(id);
        if (dialog.parentElement !== portal) portal.append(dialog);
      });
    }

    ensureAdapter() {
      const mode = 'app';
      if (this.state.adapter && this.state.adapterMode === mode) return;
      this.abortAll();
      this.state.adapterMode = mode;
      const Adapter = window.PersonalCenterApp?.Adapter;
      this.state.adapter = typeof Adapter === 'function' ? new Adapter(this) : new UnavailableAdapter();
    }

    addListener(target, type, handler, options) {
      target.addEventListener(type, handler, options);
      this.state.listeners.push({ target, type, handler, options });
    }

    addEditorListeners() {
      const section = this.closest('.shopify-section');
      this.addListener(document, 'shopify:section:load', (event) => {
        if (event.target === section) this.reinitialize();
      });
      this.addListener(document, 'shopify:section:unload', (event) => {
        if (event.target === section) this.teardown();
      });
      this.addListener(document, 'shopify:section:reorder', (event) => {
        if (event.target === section) this.reinitialize();
      });
      this.addListener(document, 'shopify:block:select', (event) => {
        const selectedId = String(event.detail?.blockId || '');
        const directTarget = event.target?.closest?.('[data-pc-config-block]');
        const blockTarget = directTarget && this.contains(directTarget)
          ? directTarget
          : Array.from(this.querySelectorAll('[data-pc-config-block]')).find((node) => selectedId && node.dataset.pcBlockId === selectedId);
        const view = blockTarget?.dataset.pcConfigBlock;
        if (!VIEWS.includes(view)) return;
        this.closeMobileNav();
        this.activateView(view, { filter: this.defaultFilter(view), history: 'none', load: true, focus: false });
      });
    }

    teardown() {
      if (!this.state.connected) return;
      this.state.logoutTrigger = null;
      this.state.logoutUrl = '';
      if (this.refs?.logoutDialog?.open) this.refs.logoutDialog.close();
      this.abortAll();
      this.disconnectModules();
      this.state.moduleTimers.forEach((timer) => window.clearTimeout(timer));
      this.state.moduleTimers.clear();
      if (this.state.headerOffsetFrame) window.cancelAnimationFrame(this.state.headerOffsetFrame);
      this.state.headerOffsetFrame = 0;
      this.state.pendingModules.clear();
      this.state.listeners.splice(0).forEach(({ target, type, handler, options }) => target.removeEventListener(type, handler, options));
      this.state.mobileNavMedia = null;
      this.state.mobileNavSync = null;
      this.state.mobileNavTarget = null;
      this.state.connected = false;
      delete this.dataset.pcEnhanced;
    }

    reinitialize() {
      if (!this.isConnected || this.state.loginBoundary) return;
      this.moveDialogsToPortal();
      this.refreshRefs();
      this.ensureAdapter();
      this.setupMobileNav();
      this.initializeRoute({ replace: true });
    }

    onPageShow(event) {
      if (event?.persisted !== true) return;
      this.revalidateSession({ force: true, refreshView: true });
    }

    onVisibilityChange() {
      if (document.visibilityState !== 'visible') return;
      this.syncStickyHeaderOffset();
      this.revalidateSession();
    }

    onHeaderOffsetChange() {
      if (this.state.headerOffsetFrame) return;
      this.state.headerOffsetFrame = window.requestAnimationFrame(() => {
        this.state.headerOffsetFrame = 0;
        this.syncStickyHeaderOffset();
      });
    }

    async revalidateSession({ force = false, refreshView = false } = {}) {
      if (!this.state.connected || this.state.loginBoundary || typeof this.state.adapter?.getOverview !== 'function') return;
      const now = Date.now();
      if (!force && now - this.state.lastSessionValidationAt < SESSION_REVALIDATION_INTERVAL_MS) return;
      this.state.lastSessionValidationAt = now;
      const previousIdentity = customerIdentity(this.state.overview);
      const request = this.beginRequest('session');
      this.dataset.pcSessionState = 'checking';
      try {
        const { response, coupons } = await this.requestOverview(request.signal);
        if (!this.isCurrent(request)) return;
        const nextIdentity = customerIdentity(response.data);
        if (previousIdentity && nextIdentity && previousIdentity !== nextIdentity) {
          window.location.reload();
          return;
        }
        this.state.overview = response.data;
        this.renderOverview(response.data, coupons);
        this.dataset.pcSessionState = 'ready';
        if (refreshView) this.loadView(this.state.activeView, { params: this.state.params[this.state.activeView] || {} });
      } catch (error) {
        if (isAbort(error) || !this.isCurrent(request)) return;
        this.dataset.pcSessionState = 'error';
        if (isAuthenticationFailure(error)) this.renderLoginBoundary();
      } finally {
        this.finishRequest(request);
      }
    }

    setupMobileNav() {
      const nav = this.refs?.mobileNav;
      if (!nav || this.state.mobileNavTarget === nav) return;
      if (this.state.mobileNavMedia && this.state.mobileNavSync) {
        this.state.mobileNavMedia.removeEventListener('change', this.state.mobileNavSync);
        this.state.listeners = this.state.listeners.filter(({ target, type, handler }) => !(target === this.state.mobileNavMedia && type === 'change' && handler === this.state.mobileNavSync));
      }
      const media = window.matchMedia('(width < 768px)');
      // The mobile summary cards are an Orders-only content block, not an
      // interactive disclosure. Keep the native details element open so its
      // panel participates in layout whenever the Orders view exposes it.
      const sync = () => { nav.open = true; };
      this.state.mobileNavMedia = media;
      this.state.mobileNavSync = sync;
      this.state.mobileNavTarget = nav;
      this.addListener(media, 'change', sync);
      sync();
    }

    isMobileNavViewport() {
      return this.state.mobileNavMedia?.matches ?? window.matchMedia('(width < 768px)').matches;
    }

    closeMobileNav({ restoreFocus = false } = {}) {
      const nav = this.refs?.mobileNav;
      if (!nav || !this.isMobileNavViewport()) return false;
      nav.open = true;
      return false;
    }

    initializeRoute({ replace = false } = {}) {
      const view = this.readRoute();
      const filter = this.readFilter(view);
      const params = this.readParams(view, filter);
      this.loadOverview();
      const requested = new URLSearchParams(window.location.search).get('pc_view');
      const normalizeDisabledDetail = requested === 'order-detail' && !orderDetailEnabled(this);
      this.activateView(view, { filter, params, history: replace || normalizeDisabledDetail ? 'replace' : 'none', load: true, focus: false });
    }

    readRoute() {
      const prepared = this.dataset.pcRouteReady === 'true' ? this.dataset.pcActiveView : '';
      const requestedValue = new URLSearchParams(window.location.search).get('pc_view');
      const requested = requestedValue === 'security' ? 'profile' : requestedValue;
      if (requested === 'order-detail' && (!orderDetailEnabled(this) || !this.readOrderId())) return 'orders';
      if (VIEWS.includes(requested)) return requested;
      if (VIEWS.includes(prepared)) return prepared;
      return VIEWS.includes(this.dataset.pcDefaultView) ? this.dataset.pcDefaultView : 'orders';
    }

    readFilter(view) {
      const allowed = FILTERS[view] || [];
      const prepared = this.dataset.pcRouteReady === 'true' ? this.dataset.pcActiveFilter : '';
      const requested = new URLSearchParams(window.location.search).get('status');
      if (allowed.includes(requested)) return requested;
      if (allowed.includes(prepared)) return prepared;
      return allowed[0] || '';
    }

    readOrderId() {
      return text(new URLSearchParams(window.location.search).get('order_id')).trim();
    }

    readParams(view, filter) {
      if (view === 'order-detail') return { orderId: this.readOrderId(), returnStatus: 'all' };
      return { status: filter };
    }

    onPopstate() {
      this.closeMobileNav();
      const view = this.readRoute();
      const filter = this.readFilter(view);
      this.activateView(view, { filter, params: this.readParams(view, filter), history: 'none', load: true, focus: true });
    }

    onModuleReady(event) {
      const readyViews = Array.isArray(event.detail?.views) ? event.detail.views : [];
      readyViews.forEach((view) => {
        const timer = this.state.moduleTimers.get(view);
        if (timer) window.clearTimeout(timer);
        this.state.moduleTimers.delete(view);
        if (!this.state.pendingModules.delete(view) || view !== this.state.activeView) return;
        this.loadView(view, { params: this.state.params[view] || { status: this.state.activeFilter } });
      });
    }

    onClick(event) {
      const clickTarget = event.target instanceof Element ? event.target : event.target?.parentElement;
      const logoutDialog = clickTarget?.closest('[data-pc-logout-dialog]');
      if (logoutDialog && this.contains(logoutDialog)) {
        const cancelButton = clickTarget.closest('[data-pc-logout-cancel]');
        const submitButton = clickTarget.closest('[data-pc-logout-submit]');
        if (cancelButton || clickTarget === logoutDialog) {
          this.dismissLogout(event);
          return;
        }
        if (submitButton) {
          this.completeLogout(event);
          return;
        }
      }

      const logoutButton = clickTarget?.closest('[data-pc-logout]');
      if (logoutButton && this.contains(logoutButton)) {
        this.requestLogout(event, logoutButton);
        return;
      }

      const link = clickTarget?.closest('[data-pc-view-link]');
      if (link && this.contains(link)) {
        if (event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const view = link.dataset.pcViewLink;
        if (!VIEWS.includes(view)) return;
        const isBottomNavigation = link.closest('.personal-center__mobile-bottom-nav') !== null;
        event.preventDefault();
        event.stopPropagation();
        this.closeMobileNav();
        this.setViewState(view, 'loading');
        this.activateView(view, {
          filter: this.defaultFilter(view),
          history: isBottomNavigation && this.state.activeView === view ? 'none' : 'push',
          load: true,
          focus: !isBottomNavigation
        });
        this.returnToPageStart(view);
        return;
      }

      const action = event.target.closest('[data-pc-action], [data-pc-page-action]');
      if (!action || !this.contains(action)) return;
      this.delegateAction(event, action);
    }

    parseLogoutUrl(value) {
      let logoutUrl;
      try {
        logoutUrl = new URL(value || '', window.location.origin);
      } catch (error) {
        return null;
      }
      const isAllowedPath = logoutUrl.pathname === '/customer_identity/logout'
        || logoutUrl.pathname === '/account/logout';
      return logoutUrl.origin === window.location.origin && isAllowedPath ? logoutUrl : null;
    }

    requestLogout(event, control) {
      event.preventDefault();
      event.stopPropagation();
      const logoutUrl = this.parseLogoutUrl(control.dataset.pcLogoutUrl);
      if (!logoutUrl) return;
      const dialog = this.refs?.logoutDialog || this.querySelector('[data-pc-logout-dialog]');
      const confirmation = text(control.dataset.pcLogoutConfirm).trim();
      if (!dialog || typeof dialog.showModal !== 'function') {
        if (!confirmation || window.confirm(confirmation)) window.location.assign(logoutUrl.href);
        return;
      }
      this.state.logoutTrigger = control;
      this.state.logoutUrl = logoutUrl.href;
      if (!dialog.open) dialog.showModal();
      const cancelButton = dialog.querySelector('.pc-logout-dialog__button--secondary');
      window.requestAnimationFrame(() => cancelButton?.focus({ preventScroll: true }));
    }

    dismissLogout(event) {
      event?.preventDefault();
      event?.stopPropagation();
      const dialog = this.refs?.logoutDialog || this.querySelector('[data-pc-logout-dialog]');
      if (dialog?.open) dialog.close('cancel');
    }

    completeLogout(event) {
      event?.preventDefault();
      event?.stopPropagation();
      const logoutUrl = this.parseLogoutUrl(this.state.logoutUrl);
      if (!logoutUrl) {
        this.dismissLogout();
        return;
      }
      const dialog = this.refs?.logoutDialog || this.querySelector('[data-pc-logout-dialog]');
      this.state.logoutTrigger = null;
      this.state.logoutUrl = '';
      if (dialog?.open) dialog.close('confirmed');
      window.location.assign(logoutUrl.href);
    }

    onLogoutDialogClose() {
      const trigger = this.state.logoutTrigger;
      this.state.logoutTrigger = null;
      this.state.logoutUrl = '';
      if (trigger?.isConnected) window.requestAnimationFrame(() => trigger.focus({ preventScroll: true }));
    }

    onChange(event) {
      const mutationForm = event.target.closest('[data-pc-mutation-form="marketing"]');
      if (mutationForm && this.contains(mutationForm)) {
        this.delegateAction(event, mutationForm);
        return;
      }
      const filter = event.target.closest('[data-pc-filter]');
      if (!filter || !this.contains(filter) || filter.checked === false) return;
      const view = filter.closest('[data-pc-filter-group]')?.dataset.pcFilterGroup || this.state.activeView;
      if (!VIEWS.includes(view) || !FILTERS[view]?.includes(filter.value)) return;
      this.activateView(view, { filter: filter.value, history: 'push', load: true, focus: false });
    }

    onSubmit(event) {
      const form = event.target.closest('[data-pc-form]');
      if (!form || !this.contains(form)) return;
      event.preventDefault();
      this.delegateAction(event, form);
    }

    onKeydown(event) {
      if (event.key === 'Escape' && this.closeMobileNav({ restoreFocus: true })) event.preventDefault();
    }

    delegateAction(event, action) {
      const view = action.closest('[data-pc-view]')?.dataset.pcView
        || action.closest('[data-pc-dialog-owner]')?.dataset.pcDialogOwner
        || this.state.activeView;
      const module = this.moduleFor(view);
      if (!module || typeof module.handleAction !== 'function') {
        event.preventDefault();
        this.failClosed(action, 'Esta acción no está disponible.');
        return;
      }
      try {
        const result = module.handleAction({ event, action, root: this.panelFor(view), client: this.state.adapter, shell: this });
        if (result && typeof result.then === 'function') {
          result.then((resolved) => this.applyActionResult(event, action, view, resolved)).catch((error) => {
            if (!isAbort(error)) this.reportActionError(view, error);
          });
          return;
        }
        this.applyActionResult(event, action, view, result);
      } catch (error) {
        if (!isAbort(error)) this.reportActionError(view, error);
      }
    }

    reportActionError(view, error) {
      if (isAuthenticationFailure(error)) {
        this.renderLoginBoundary();
        return;
      }
      this.reportError(view, error);
    }

    applyActionResult(event, action, view, result) {
      if (result?.navigate === true) {
        event.preventDefault();
        const nextView = VIEWS.includes(result.view) ? result.view : view;
        const nextFilter = result.params?.status || result.params?.returnStatus || this.defaultFilter(nextView);
        this.activateView(nextView, {
          filter: nextFilter,
          params: result.params,
          history: result.history === 'replace' ? 'replace' : 'push',
          load: true,
          focus: result.focus !== false
        });
        return;
      }
      const hasParams = result?.params && typeof result.params === 'object';
      if (result?.preventDefault === true || result?.unavailable === true || hasParams) event.preventDefault();
      if (result?.unavailable === true) {
        this.failClosed(action, text(result.message).trim() || 'Esta acción no está disponible.');
        return;
      }
      if (hasParams) this.reload(result.view || view, result.params);
    }

    defaultFilter(view) {
      return FILTERS[view]?.[0] || '';
    }

    activateView(view, { filter, params, history = 'none', load = true, focus = false } = {}) {
      const nextView = VIEWS.includes(view) && (view !== 'order-detail' || orderDetailEnabled(this)) ? view : 'orders';
      const nextFilter = FILTERS[nextView]?.includes(filter) ? filter : this.defaultFilter(nextView);
      const nextParams = params && typeof params === 'object' ? Object.assign({}, params) : { status: nextFilter };
      if (nextView === 'order-detail') {
        nextParams.orderId = text(nextParams.orderId).trim();
        nextParams.returnStatus = FILTERS.orders.includes(nextParams.returnStatus) ? nextParams.returnStatus : 'all';
        if (!nextParams.orderId) return this.activateView('orders', { filter: 'all', history: 'replace', load, focus });
      }
      const previous = this.state.activeView;
      if (previous && previous !== nextView) {
        this.abortView(previous);
        this.disconnectModule(previous);
      }
      this.state.activeView = nextView;
      this.state.activeFilter = nextFilter;
      this.dataset.pcActiveView = nextView;
      this.dataset.pcActiveFilter = nextFilter;
      this.refs.views.forEach((panel) => {
        const active = panel.dataset.pcView === nextView;
        panel.hidden = !active;
        panel.setAttribute('aria-hidden', String(!active));
      });
      this.refs.links.forEach((link) => {
        const active = link.dataset.pcViewLink === (nextView === 'order-detail' ? 'orders' : nextView);
        link.classList.toggle('is-active', active);
        if (active) link.setAttribute('aria-current', 'page');
        else link.removeAttribute('aria-current');
      });
      this.querySelectorAll('[data-pc-filter]').forEach((control) => {
        const group = control.closest('[data-pc-filter-group]')?.dataset.pcFilterGroup;
        if (group === nextView) control.checked = control.value === nextFilter;
      });
      this.writeHistory(nextView, nextFilter, history, nextParams);
      if (load) this.loadView(nextView, { params: nextParams });
      if (focus) this.focusView(nextView);
    }

    writeHistory(view, filter, mode, params = {}) {
      if (mode === 'none') return;
      const url = new URL(window.location.href);
      url.searchParams.set('pc_view', view);
      if (view === 'order-detail') {
        url.searchParams.set('order_id', text(params.orderId).trim());
        url.searchParams.delete('status');
      } else {
        url.searchParams.delete('order_id');
        if (filter) url.searchParams.set('status', filter);
        else url.searchParams.delete('status');
      }
      const state = { personalCenterView: view, personalCenterFilter: filter || null, personalCenterParams: params };
      if (mode === 'replace') window.history.replaceState(state, '', url);
      if (mode === 'push') window.history.pushState(state, '', url);
    }

    focusView(view) {
      const panel = this.panelFor(view);
      const target = panel?.querySelector('h1, h2, [data-pc-view-heading]') || panel;
      if (target && !target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      target?.focus?.({ preventScroll: true });
    }

    /**
     * Brings the panel into view after a view switch - but only when it is not already there.
     *
     * This used to scroll twice on consecutive frames: once to the raw component top, then
     * again to the same point minus the sticky header. Both landed, so every click on the
     * left-hand nav produced a visible two-step jump even when the panel had never left the
     * viewport. Switching tabs is not navigation and should not move the page under the
     * reader's eyes.
     *
     * Now the offset is resolved first and applied in a single scroll, and the scroll only
     * happens when the panel's top is actually hidden behind the sticky header or above the
     * fold. `focusView` uses `preventScroll`, so moving focus never reintroduces a jump.
     */
    returnToPageStart(view) {
      window.requestAnimationFrame(() => {
        if (!this.state.connected || this.state.activeView !== view) return;

        const headerOffset = this.syncStickyHeaderOffset();
        const clearance = this.scrollClearance();
        const safeTop = headerOffset + clearance;
        const rect = this.getBoundingClientRect();

        // Already sitting below the sticky header: leave the page exactly where it is.
        if (rect.top >= 0 && rect.top <= safeTop + 1) {
          this.focusView(view);
          return;
        }

        const target = Math.max(0, Math.round(window.scrollY + rect.top - safeTop));
        if (Math.abs(window.scrollY - target) > 1) {
          window.scrollTo({ top: target, left: 0, behavior: 'auto' });
        }
        this.focusView(view);
      });
    }

    stickyHeaderOffset() {
      const selector = '.header-sticky, [data-header-sticky], [data-sticky-header], single-banner, .announcement-bar-main';
      return Array.from(document.querySelectorAll(selector)).reduce((offset, header) => {
        const style = window.getComputedStyle(header);
        if (style.position !== 'sticky' && style.position !== 'fixed') return offset;
        const rect = header.getBoundingClientRect();
        if (rect.top > 1 || rect.bottom <= 0) return offset;
        return Math.max(offset, Math.ceil(rect.bottom));
      }, 0);
    }

    syncStickyHeaderOffset() {
      const offset = this.stickyHeaderOffset();
      this.style.setProperty('--pc-header-offset', `${offset}px`);
      return offset;
    }

    scrollClearance() {
      const value = Number.parseFloat(window.getComputedStyle(this).getPropertyValue('--pc-scroll-clearance'));
      return Number.isFinite(value) ? Math.max(0, value) : 0;
    }

    panelFor(view) {
      return this.refs?.views?.find((panel) => panel.dataset.pcView === view) || null;
    }

    moduleFor(view) {
      const module = currentModules()[view];
      return module && typeof module === 'object' ? module : null;
    }

    beginRequest(view) {
      this.abortView(view);
      const controller = new AbortController();
      const request = { view, controller, signal: controller.signal, intent: ++this.state.intent };
      this.state.controllers.set(view, request);
      return request;
    }

    isCurrent(request) {
      return this.state.controllers.get(request.view) === request && request.signal.aborted === false;
    }

    finishRequest(request) {
      if (this.state.controllers.get(request.view) === request) this.state.controllers.delete(request.view);
    }

    abortView(view) {
      const request = this.state.controllers.get(view);
      if (request) request.controller.abort();
      this.state.controllers.delete(view);
    }

    abortAll() {
      Array.from(this.state.controllers.keys()).forEach((view) => this.abortView(view));
    }

    async requestOverview(signal) {
      const [overviewResult, couponResult] = await Promise.allSettled([
        this.state.adapter.getOverview({ signal }),
        typeof this.state.adapter.listCoupons === 'function'
          ? this.state.adapter.listCoupons({ status: 'available', signal })
          : Promise.resolve(null)
      ]);
      if (overviewResult.status === 'rejected') throw overviewResult.reason;
      return {
        response: overviewResult.value,
        coupons: couponResult.status === 'fulfilled' ? couponResult.value : null
      };
    }

    async loadOverview() {
      if (!this.state.connected || this.state.loginBoundary) return;
      if (typeof this.state.adapter?.getOverview !== 'function') {
        this.announce('El resumen de cuenta no está disponible.', 'info');
        return;
      }
      const request = this.beginRequest('overview');
      try {
        const { response, coupons } = await this.requestOverview(request.signal);
        if (!this.isCurrent(request)) return;
        this.state.overview = response.data;
        this.state.lastSessionValidationAt = Date.now();
        this.renderOverview(response.data, coupons);
      } catch (error) {
        if (isAbort(error) || !this.isCurrent(request)) return;
        if (isAuthenticationFailure(error)) {
          this.renderLoginBoundary();
          return;
        }
        if (!this.state.overview) this.renderOverview(null);
        this.announce('', 'empty');
      } finally {
        this.finishRequest(request);
      }
    }

    renderOverview(data, couponEnvelope) {
      const profile = data?.profile;
      const displayName = text(profile?.displayName).trim();
      if (displayName) this.setBoundValue('bootstrap.customer.displayName', displayName);
      this.setSummaryValue('credits', formatMoney(data?.credit?.balance));
      this.setSummaryValue('coupons', couponEnvelope?.meta?.counts?.available);
      this.setMobileNavigationBadge('orders', data?.orderCounts?.in_transit);
      this.setMobileNavigationBadge('coupons', couponEnvelope?.meta?.counts?.available);
    }

    setBoundValue(key, value) {
      const output = text(value);
      this.querySelectorAll(`[data-pc-bind="${key}"]`).forEach((node) => {
        node.textContent = output;
        node.dataset.pcValue = output;
      });
      if (key === 'bootstrap.customer.displayName') {
        const initial = output.trim().charAt(0).toUpperCase();
        if (!initial) return;
        this.querySelectorAll('[data-pc-avatar-initial]').forEach((node) => { node.textContent = initial; });
      }
    }

    setSummaryValue(key, value) {
      const output = value === null || value === undefined || value === '' ? '—' : text(value);
      this.querySelectorAll(`[data-pc-summary="${key}"]`).forEach((node) => {
        node.textContent = output;
        node.dataset.pcValue = output;
      });
    }

    setMobileNavigationBadge(key, value) {
      const count = Number(value);
      const roundedCount = Number.isFinite(count) ? Math.floor(count) : 0;
      const visible = roundedCount > 0;
      const displayValue = roundedCount > 99 ? '99+' : String(roundedCount);
      const accessibleValue = roundedCount > 99 ? '99 o más' : String(roundedCount);
      const suffix = key === 'orders' ? 'pedidos en camino' : key === 'coupons' ? 'cupones disponibles' : '';
      this.querySelectorAll(`[data-pc-mobile-nav-badge="${key}"]`).forEach((badge) => {
        badge.hidden = !visible;
        badge.textContent = visible ? displayValue : '';
      });
      this.querySelectorAll(`.personal-center__mobile-bottom-nav [data-pc-view-link="${key}"]`).forEach((link) => {
        const label = text(link.querySelector('.personal-center__mobile-bottom-nav-label')?.textContent).trim();
        if (visible && label && suffix) link.setAttribute('aria-label', `${label}: ${accessibleValue} ${suffix}`);
        else link.removeAttribute('aria-label');
      });
    }

    async loadView(view, { params = {} } = {}) {
      if (!this.state.connected || this.state.loginBoundary || view !== this.state.activeView) return;
      const panel = this.panelFor(view);
      const module = this.moduleFor(view);
      if (panel && !module) {
        this.state.params[view] = Object.assign({}, params);
        this.state.pendingModules.add(view);
        this.setViewState(view, 'loading');
        this.announce('Cargando información de cuenta.', 'loading');
        const existingTimer = this.state.moduleTimers.get(view);
        if (existingTimer) window.clearTimeout(existingTimer);
        this.state.moduleTimers.set(view, window.setTimeout(() => {
          this.state.moduleTimers.delete(view);
          if (!this.state.pendingModules.delete(view) || view !== this.state.activeView) return;
          this.setViewState(view, 'error');
          this.announce('No se pudo preparar esta sección de cuenta.', 'error');
        }, 10000));
        return;
      }
      if (!panel || typeof module?.load !== 'function' || typeof module?.render !== 'function') {
        this.setViewState(view, 'error');
        this.announce('No se pudo preparar esta sección de cuenta.', 'error');
        return;
      }

      this.state.pendingModules.delete(view);
      const moduleTimer = this.state.moduleTimers.get(view);
      if (moduleTimer) window.clearTimeout(moduleTimer);
      this.state.moduleTimers.delete(view);

      const request = this.beginRequest(view);
      this.state.params[view] = Object.assign({}, params);
      this.setViewState(view, 'loading');
      this.announce('Cargando información de cuenta.', 'loading');
      try {
        const response = await module.load({ client: this.state.adapter, root: panel, params: this.state.params[view], signal: request.signal });
        if (!this.isCurrent(request)) return;
        const rendered = await module.render({ root: panel, response, shell: this });
        if (!this.isCurrent(request)) return;
        const state = requestedState(rendered, response);
        this.setViewState(view, state);
        this.announce(state === 'partial' ? 'Se muestra la información disponible.' : '', state);
      } catch (error) {
        if (isAbort(error) || !this.isCurrent(request)) return;
        if (isAuthenticationFailure(error)) {
          this.renderLoginBoundary();
          return;
        }
        this.reportReadFailure(view, error, module, panel);
      } finally {
        this.finishRequest(request);
      }
    }

    reload(view = this.state.activeView, params = this.state.params[view] || {}) {
      if (!VIEWS.includes(view)) return;
      if (view !== this.state.activeView) this.activateView(view, { filter: params.status || this.defaultFilter(view), history: 'none', load: false });
      this.loadView(view, { params });
    }

    reportReadFailure(view, error, module, panel) {
      if (panel) panel.dataset.pcErrorCode = text(error?.code);
      module?.renderReadFailure?.({ root: panel, params: this.state.params[view] || {}, shell: this });
      this.setViewState(view, 'error');
      this.announce('', 'error');
    }

    reportError(view, error) {
      const message = text(error?.message).trim() || 'No se pudo cargar la información de cuenta.';
      const panel = this.panelFor(view);
      if (panel) {
        panel.dataset.pcErrorCode = text(error?.code);
        panel.querySelectorAll('[data-pc-error-message]').forEach((target) => { target.textContent = message; });
      }
      this.setViewState(view, 'error');
      this.announce('', 'error');
    }

    /**
     * Holds the panel's rendered height while a view loads.
     *
     * The loading state has a fixed `--pc-view-state-min-block-size` (about 420-540px), so
     * leaving a tall panel - an order list, a long transaction table - collapses the page by
     * hundreds of pixels the instant the switch begins, and expands it again when the data
     * lands. That double reflow is what reads as the whole page jumping around.
     *
     * The floor is released as soon as real content is in, so a genuinely shorter view still
     * settles to its own height; it only has to survive the gap in between.
     */
    holdPanelHeight(panel, state) {
      if (state === 'loading') {
        const height = Math.round(panel.getBoundingClientRect().height);
        // Nothing worth holding before the panel has ever rendered.
        if (height > 0) panel.style.setProperty('--pc-view-hold-block-size', `${height}px`);
        return;
      }
      // Wait a frame: clearing in the same frame the content arrives lets the browser paint
      // the collapsed height once before the new content measures.
      window.requestAnimationFrame(() => {
        panel.style.removeProperty('--pc-view-hold-block-size');
      });
    }

    setViewState(view, state) {
      const panel = this.panelFor(view);
      const next = STATES.includes(state) ? state : 'error';
      if (!panel) return;
      this.holdPanelHeight(panel, next);
      const activeElement = document.activeElement;
      const stateOutputs = Array.from(panel.querySelectorAll('[data-pc-state]'));
      const derivedOutputs = Array.from(panel.querySelectorAll('[data-pc-state-output]:not([data-pc-state])'));
      const hidesFocusedOutput = [...stateOutputs, ...derivedOutputs].some((output) => {
        const outputState = output.dataset.pcState || output.dataset.pcStateOutput;
        return outputState !== next && output.contains(activeElement);
      });
      panel.dataset.pcViewState = next;
      panel.dataset.pcStateOutput = next;
      stateOutputs.forEach((output) => {
        output.dataset.pcStateOutput = next;
        output.hidden = output.dataset.pcState !== next;
      });
      derivedOutputs.forEach((output) => {
        output.hidden = output.dataset.pcStateOutput !== next;
      });
      if (hidesFocusedOutput && view === this.state.activeView) this.focusView(view);
    }

    announce(message, type) {
      if (!this.refs?.status) return;
      this.refs.status.textContent = message;
      this.refs.status.dataset.pcStatusType = type;
    }

    failClosed(action, message) {
      if ('disabled' in action) action.disabled = true;
      action.setAttribute('aria-disabled', 'true');
      this.announce(message, 'error');
    }

    disconnectModule(view) {
      const module = this.moduleFor(view);
      const root = this.panelFor(view);
      if (module && root && typeof module.disconnect === 'function') module.disconnect({ root });
    }

    disconnectModules() {
      VIEWS.forEach((view) => this.disconnectModule(view));
    }

    renderLoginBoundary() {
      if (this.state.loginBoundary) return;
      this.state.loginBoundary = true;
      this.state.overview = null;
      this.abortAll();
      this.disconnectModules();
      const template = this.querySelector('template[data-pc-login-template]');
      const shell = this.querySelector('.personal-center__shell');
      const mobileNavigation = this.querySelector('.personal-center__mobile-bottom-nav');
      if (template && shell) shell.replaceWith(template.content.cloneNode(true));
      mobileNavigation?.remove();
    }
  }

  if (!customElements.get('personal-center-shell')) customElements.define('personal-center-shell', PersonalCenterShell);
})();
